import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion"
import { Shield, Eye, Lock, Users, Database, Globe, Scale, Cookie, MailIcon } from "lucide-react"
import { HomepageLoader } from "../components/Homepage/HomepageLoader";
import SeoHead from "../seo/SeoHead";
import { generateMetadata } from "../seo/metadataGenerator";
import { PAGE_OG_IMAGES, SITE_URL } from "../seo/constants";
import { generateBreadcrumbSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export const PrivacyPolicy = () => {
  usePrerenderReady();
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const updateCursorPosition = useCallback((e) => {
    requestAnimationFrame(() => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  useEffect(() => {
    document.body.style.cursor = "none";
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const interactiveElements = document.querySelectorAll(
      'a, button, input, [role="button"]'
    );
    interactiveElements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    window.addEventListener("mousemove", updateCursorPosition);

    // Simulate loading time (remove this in production and use real loading logic)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.body.style.cursor = "auto";
      window.removeEventListener("mousemove", updateCursorPosition);
      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [updateCursorPosition]);

  const metadata = generateMetadata({
    title: "Privacy Policy | UpNComer",
    description: "Learn how UpNComer collects, uses, and protects your personal information.",
    image: PAGE_OG_IMAGES.helpCenter,
    path: "/privacy-policy",
  });

  const webPageSchema = generateWebPageSchema({
    title: "Privacy Policy | UpNComer",
    description: "Learn how UpNComer collects, uses, and protects your personal information.",
    url: `${SITE_URL}/privacy-policy`,
  });

  const schemas = generateGraphSchema([
    webPageSchema,
    generateBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Privacy Policy", path: "/privacy-policy" },
    ]),
  ]);

  return (
    <>
      <SeoHead metadata={metadata} schemas={schemas} />
      <div
        className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%)`,
        }}
      >
        <div className="inner-circle"></div>
      </div>
      <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
      {/* Enhanced Multi-layered Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

        {/* Floating orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
          className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
        />
      </div>

      <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
        <div className="container max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                <Shield className="w-12 h-12 text-[#FA6138]" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
              Privacy Policy
            </h1>
            <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed">
              Your privacy is our priority. Learn how we collect, use, and protect your information.
            </p>
            <div className="text-[#818cf8]/60 text-sm mt-4"><strong>Last updated: November 23, 2025</strong></div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8 md:p-12"
            style={{
              boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
            }}
          >
            <div className="prose prose-invert max-w-none">
              <div className="space-y-8">
                {/* Introduction */}
                <section>
                  <div className="flex items-center mb-4">
                    <Eye className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Introduction</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    At <strong>UpNComer Pte. Ltd.</strong> (“we,” “our,” or “us”), we respect your privacy and are committed to protecting your personal data.<br/>
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, web and mobile apps, and related services (collectively, the “Service”).<br/>
                    By using our Service, you agree to the practices described below.
                  </p>
                </section>

                {/* Information We Collect */}
                <section>
                  <div className="flex items-center mb-4">
                    <Database className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Information We Collect</h2>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Personal Information</h3>
                      <p className="text-[#818cf8]/80 leading-relaxed mb-2">
                        We may collect personal information that you provide directly to us, including:
                      </p>
                      <ul className="list-disc list-inside text-[#818cf8]/80 space-y-1 ml-4">
                        <li>Name, email address, and contact details</li>
                        <li>Account credentials and profile information</li>
                        <li>Payment and billing details (handled securely via third-party providers)</li>
                        <li>Uploaded music files, metadata, images, and creative content</li>
                        <li>Communications with our support or partnerships team</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#a5b4fc] mb-2">Usage Information</h3>
                      <p className="text-[#818cf8]/80 leading-relaxed mb-2">
                        We automatically collect data on how you interact with our Service:
                      </p>
                      <ul className="list-disc list-inside text-[#818cf8]/80 space-y-1 ml-4">
                        <li>Device information and IP address</li>
                        <li>Browser type, operating system, and version</li>
                        <li>Pages visited, features used, and session duration</li>
                        <li>Referral sources and search terms</li>
                        <li>General analytics and engagement data</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* How We Use Your Information */}
                <section>
                  <div className="flex items-center mb-4">
                    <Users className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">How We Use Your Information</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                    We use collected information for the following purposes:
                  </p>
                  <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                    <li>Providing, maintaining, and improving our Service</li>
                    <li>Processing transactions and managing accounts</li>
                    <li>Personalizing your experience and recommendations</li>
                    <li>Sending important notices, updates, and support communications</li>
                    <li>Analyzing usage trends to improve performance</li>
                    <li>Detecting and preventing fraud or security threats</li>
                    <li>Complying with legal obligations and enforcing our Terms</li>
                  </ul>
                </section>

                {/* Information Sharing */}
                <section>
                  <div className="flex items-center mb-4">
                    <Globe className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Third-Party Integrations & Data Sharing</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                    We do not <strong>sell or rent</strong> your personal information.<br/>
                    We may share data only when necessary:
                  </p>
                  <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                    <li>
                      <strong>Advertising & Analytics Partners:</strong> When you connect Meta (Facebook/Instagram), YouTube, TikTok, Spotify, Apple Music, or Feature.fm accounts, we access only what’s required for analytics, campaign automation, or link creation. Tokens are encrypted and never shared.
                    </li>
                    <li>
                      <strong>Service Providers:</strong> Hosting (AWS, Vercel), payment (Stripe), communication, and analytics tools help us operate the platform.
                    </li>
                    <li>
                      <strong>Business Transfers:</strong> In a merger or acquisition, your data may transfer under the same protections.
                    </li>
                    <li>
                      <strong>Legal Requirements:</strong> When required by law or to protect user rights.
                    </li>
                    <li>
                      <strong>With Consent:</strong> For any new or optional purposes.
                    </li>
                  </ul>
                </section>

                {/* Data Security */}
                <section>
                  <div className="flex items-center mb-4">
                    <Lock className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">AI & Data Analytics</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                    We use AI systems such as <strong>Amplitude AI</strong> to provide personalized insights and recommendations.
                  </p>
                  <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                    <li>
                      Data used for analytics is <strong>aggregated and anonymized</strong>.
                    </li>
                    <li>
                      Your creative content is never used to train models without explicit consent.
                    </li>
                    <li>
                      Insights are generated exclusively for your account and not shared across users.
                    </li>
                  </ul>
                </section>
                <section>
                  <div className="flex items-center mb-4">
                    <Eye className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">User-Generated Content & Ownership</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    You retain full ownership of your music, artwork, and creative content.<br/>
By uploading material, you grant us a limited, revocable license to store, process, and display it only to deliver the Service.<br/>
We do not claim ownership or commercial rights over your works.
                  </p>
                </section>
                <section>
                  <div className="flex items-center mb-4">
                    <Lock className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Advertising & Campaign Data</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                    When using the <strong>Growth Engine</strong> module to run ads:
                  </p>
                  <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                    <li>
                      We process campaign IDs, budgets, ad sets, and metrics
                    </li>
                    <li>
                      We access audience parameters (age, gender, location, interests)
                    </li>
                    <li>
                      Data is used solely to help you manage or analyze your campaigns
                    </li>
                    <li>
                      We will never run or modify ads without your explicit authorization.
                    </li>
                  </ul>
                </section>
                <section>
                  <div className="flex items-center mb-4">
                    <Lock className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Data Retention</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    We retain data only as long as necessary to provide our Service or comply with obligations.<br/>
Music files, analytics data, and user records are deleted within <strong>30 days of account closure</strong>, unless you request otherwise.
You can request deletion anytime via <a href="mailto:support@upncomer.co" className="text-[#FA6138] font-bold hover:underline">
                        support@upncomer.co
                      </a>.
                  </p>
                </section>

                <section>
                  <div className="flex items-center mb-4">
                    <Globe className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">International Data Transfers</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    Your data may be processed in Singapore, Canada, or the United States.
We use recognized safeguards, such as Standard Contractual Clauses and PDPA compliance measures, to ensure equivalent protection across regions.
                  </p>
                </section>
                <section>
                  <div className="flex items-center mb-4">
                    <Lock className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Data Security</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                    We apply industry-standard security measures, including:
                  </p>
                  <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                    <li>
                      End-to-end encryption and TLS protection
                    </li>
                    <li>
                      Secure servers and restricted access
                    </li>
                    <li>
                      Regular penetration testing and audits
                    </li>
                    <li>
                      While no method of transmission is 100% secure, we continually improve our systems to protect your data.
                    </li>
                  </ul>
                </section>

                {/* Your Rights */}
                <section>
                  <div className="flex items-center mb-4">
                    <Scale className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Your Rights and Choices</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                    Depending on your jurisdiction, you may have the right to:
                  </p>
                  <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                    <li>
                      <strong>Access</strong> your personal information
                    </li>
                    <li>
                      <strong>Correct</strong> inaccurate or outdated information
                    </li>
                    <li>
                      <strong>Delete</strong> your data (“right to be forgotten”)
                    </li>
                    <li>
                      <strong>Port</strong> your data to another provider
                    </li>
                    <li>
                      <strong>Withdraw Consent</strong> for processing or marketing
                    </li>
                  </ul>
                  <p className="text-[#818cf8]/80 leading-relaxed mt-4">
                    Contact <a href="mailto:support@upncomer.co" className="text-[#FA6138] hover:underline">
                      support@upncomer.co
                    </a> to exercise any of these rights.<br/>
                    We’ll respond within <strong>30 days</strong> where required by law.
                  </p>
                </section>

                {/* Cookies */}
                <section>
                  <div className="flex items-center mb-4">
                    <Cookie className="w-6 h-6 text-[#FA6138] mr-3" />
                    <h2 className="text-2xl font-bold text-[#c7d2fe]">Cookies and Tracking Technologies</h2>
                  </div>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    We use cookies, pixels, and similar tools to improve your experience and analyze traffic.
You can manage cookie preferences through your browser settings.
Learn more in our{" "}
                    <a href="/cookie-policy" className="text-[#FA6138] hover:underline">
                      Cookie Policy
                    </a>
                    .
                  </p>
                </section>

                {/* International Transfers */}

                {/* Children's Privacy */}
                <section>
                  <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Children's Privacy</h2>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    Our Service is not directed at children under 13.<br/>
We do not knowingly collect personal information from minors.<br/>
If you believe a child has submitted data, contact us immediately for removal.
                  </p>
                </section>

                {/* Changes to Policy */}
                <section>
                  <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Changes to This Privacy Policy</h2>
                  <p className="text-[#818cf8]/80 leading-relaxed">
                    We may update this Policy periodically.<br/>
Changes will appear on this page with a revised “Last updated” date.<br/>
Material changes will also be communicated by email or in-app notification.<br/>
Continued use after updates means acceptance of the revised Policy.
                  </p>
                </section>

                {/* Contact Information */}
                <section>
                  <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Contact Us</h2>
                  <div className="bg-[#1e3a8a]/20 rounded-lg p-4 border border-[#3b82f6]/30">
                    <p className="text-[#c7d2fe] mb-2">
                      <strong>UpNComer Pte. Ltd.</strong>
                    </p>
                    <p className="text-[#818cf8]/80">6 Marina Boulevard, 11-18, Singapore 018985</p>
                      <p className="text-[#818cf8]/80 flex items-center gap-2">
                        <MailIcon />
                        <a
                          href="mailto:support@upncomer.co"
                          className="text-[#FA6138] hover:underline"
                        >
                          support@upncomer.co
                        </a>
                      </p>
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};
